/**
 * Context React per il ramo operativo (unità organizzativa) selezionato.
 *
 * Gestisce:
 * - Il ramo corrente selezionato dall'utente
 * - La lista dei moduli abilitati per quel ramo
 * - Lo switch tra rami (chiamata POST /api/v1/auth/switch-uo)
 *
 * Se l'utente non ha rami assegnati, il contesto è inattivo
 * e tutti i moduli restano visibili (comportamento legacy).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, type UOBrief } from './AuthContext';
import { registeredModules } from '../registry/modules';

interface UOContextValue {
  /** Ramo operativo attualmente selezionato, null se nessuno. */
  currentUO: UOBrief | null;
  /** Lista rami disponibili per l'utente corrente. */
  availableUOs: UOBrief[];
  /** Nomi dei moduli backend abilitati per il ramo corrente. */
  enabledModules: string[];
  /** True durante il caricamento dei moduli. */
  loading: boolean;
  /** Cambia ramo operativo. Emette un nuovo JWT e ricarica il profilo. */
  switchUO: (uoId: string) => Promise<void>;
}

const UOContext = createContext<UOContextValue>({
  currentUO: null,
  availableUOs: [],
  enabledModules: [],
  loading: false,
  switchUO: async () => {},
});

/** Trova il backendModulo associato alla route corrente, null se sistema. */
function findBackendModulo(pathname: string): string | null {
  for (const mod of registeredModules) {
    if (!mod.backendModulo) continue;
    for (const route of mod.routes) {
      // Match esatto o parametrica: /cassa matches /cassa, /cassa/vendita, /cassa/scontrini/:id
      const base = route.path.split('/:')[0]; // es. '/cassa/scontrini'
      if (pathname === base || pathname.startsWith(base + '/')) {
        return mod.backendModulo;
      }
    }
  }
  return null;
}


const UOProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, reload } = useAuth();
  const [enabledModules, setEnabledModules] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const availableUOs = user?.unita_organizzative ?? [];
  const currentUO = availableUOs.find(
    (uo) => uo.id === user?.unita_organizzativa_id
  ) ?? null;

  // Fetch moduli abilitati quando cambia la UO
  const fetchModuli = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token || !user?.unita_organizzativa_id) {
      setEnabledModules([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/v1/auth/me/moduli', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEnabledModules(data.moduli ?? []);
      }
    } catch {
      /* Errore di rete — manteniamo lo stato corrente. */
    } finally {
      setLoading(false);
    }
  }, [user?.unita_organizzativa_id]);

  useEffect(() => {
    fetchModuli();
  }, [fetchModuli]);

  // Protezione navigazione diretta: se l'URL corrente punta a un modulo
  // non abilitato per il ramo selezionato, redirect alla Dashboard.
  useEffect(() => {
    if (loading || !currentUO || enabledModules.length === 0) return;
    const currentModulo = findBackendModulo(location.pathname);
    if (currentModulo && !enabledModules.includes(currentModulo)) {
      navigate('/prezzi', { replace: true });
    }
  }, [enabledModules, currentUO, loading, location.pathname, navigate]);

  const switchUO = useCallback(async (uoId: string) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch('/api/v1/auth/switch-uo', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uo_id: uoId }),
      });
      if (!res.ok) return;

      const data = await res.json();
      localStorage.setItem('token', data.access_token);

      // Ricarica profilo con il nuovo token (aggiorna user.unita_organizzativa_id)
      await reload();

      // Sempre alla Dashboard dopo switch ramo — l'utente deve accorgersi del cambio
      navigate('/prezzi', { replace: true });
    } catch {
      /* Errore di rete */
    }
  }, [reload, navigate]);

  return (
    <UOContext.Provider
      value={{ currentUO, availableUOs, enabledModules, loading, switchUO }}
    >
      {children}
    </UOContext.Provider>
  );
};

/** Hook per accedere al contesto del ramo operativo. */
const useUO = (): UOContextValue => useContext(UOContext);

export { UOProvider, useUO };
